import { FC, useState, useEffect, useMemo } from 'react';
import { useGateways, isHardwareGateway } from '../../context/GatewayContext';
import { GatewayIcon, CloudGatewayIcon, MeterIcon } from '../../components/icons/icons';
import { AnimatedBirdIcon } from '../../components/icons/AnimatedBirdIcon';
import { PowerMeter } from '../../components/PowerMeter';
import PageContainer from '../../components/layout/PageContainer';

interface MeterSettings {
  fuseSize: number;
}

const MeterPage: FC = () => {
  const { gateways } = useGateways();
  const [settings] = useState<MeterSettings>(() => {
    return {
      fuseSize: 25,
    };
  });

  // Calculate maximum power based on settings (using line-to-neutral voltage)
  const maxPowerKw = (3 * 230 * settings.fuseSize) / 1000;

  // Save settings to localStorage when they change
  useEffect(() => {
    localStorage.setItem('meterSettings', JSON.stringify(settings));
  }, [settings]);

  // Filter and process meters from all gateways
  const meters = useMemo(() => 
    gateways?.flatMap(gateway => 
      (gateway.ders || [])
        .filter(der => der.type.toLowerCase().includes('meter'))
        .map(der => ({
          ...der,
          gatewayName: gateway.name,
          gatewayId: gateway.id
        }))
    ) || [], [gateways]
  );

  // Add function to get gateway state
  const getGatewayState = (gatewayId: string) => {
    const gateway = gateways?.find(g => g.id === gatewayId);
    if (!gateway || !gateway.state?.timestamp) return 'unknown';
    
    const now = Date.now();
    const diffMinutes = (now - gateway.state.timestamp) / 1000 / 60;
    
    if (diffMinutes < 10) return 'active';
    if (diffMinutes > 60) return 'inactive';
    return 'disabled';
  };

  return (
    <PageContainer>
      <div className="mb-6 sm:mb-8 mt-1 sm:mt-1">
        <div className="flex items-center gap-2 mb-2 sm:mb-4">
          <div className="flex items-center gap-2">
            <MeterIcon className="w-5 h-5 text-[#00FF84]" state="active" />
            <h1 className="text-xl font-bold text-white">Meter Data</h1>
          </div>
        </div>
      </div>

      <div className="h-px bg-[#292929] w-full mb-3 -mt-3"></div>

      {/* Connected Meters List */}
      <div className="space-y-4 sm:space-y-8">
        {meters.length > 0 ? (
          meters
            .sort((a, b) => new Date(b.lastSeen).getTime() - new Date(a.lastSeen).getTime())
            .map((meter) => {
              const gateway = gateways?.find(g => g.id === meter.gatewayId);
              return (
                <div key={meter.sn} className="space-y-4">
                  <div className="flex items-center gap-2">
                    {gateway && (isHardwareGateway(gateway) ? (
                      <GatewayIcon state={getGatewayState(meter.gatewayId)} className="w-5 h-5" />
                    ) : (
                      <CloudGatewayIcon state={getGatewayState(meter.gatewayId)} className="w-5 h-5" />
                    ))}
                    <h2 className="text-xl font-medium text-white">{meter.gatewayName}</h2>
                  </div>
                  <PowerMeter
                    lastSeen={meter.lastSeen}
                    sn={meter.sn}
                    gatewayName={meter.gatewayName}
                    gatewayId={meter.gatewayId}
                    maxPowerKw={maxPowerKw}
                  />
                </div>
              );
            })
        ) : (
          <div className="bg-[#1D1D1D] rounded-[8px] p-6 border border-[#292929] text-center min-h-[calc(100vh-12rem)] flex flex-col justify-center">
            <AnimatedBirdIcon className="w-12 h-12 mx-auto text-[#00FF84]" />
            <h3 className="mt-4 text-lg font-medium text-gray-300">No Energy Meters Found</h3>
            <p className="mt-2 text-gray-400">No energy meters are currently connected to any of your gateways.</p>
            <a 
              href="https://sourceful.energy/zap" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="mt-6 inline-block mx-auto px-6 py-3 bg-[#00FF84] hover:bg-[#00CC6A] text-black font-medium rounded-full transition-colors"
            >
              Get your Sourceful Energy Zap
            </a>
          </div>
        )}
      </div>
    </PageContainer>
  );
};

export default MeterPage; 